import { rawAttr, stringAttr } from "@wiki/core";
import type { Mark, MarkType } from "prosemirror-model";
import { toggleMark } from "prosemirror-commands";
import { TextSelection, type Command, type EditorState } from "prosemirror-state";

import { marks, nodes } from "../schema";

export function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  if (empty) return type.isInSet(state.storedMarks ?? $from.marks()) !== undefined;
  return state.doc.rangeHasMark(from, to, type);
}

/** The mark of this type covering the caret, with its attributes. */
export function markAt(state: EditorState, type: MarkType): Mark | null {
  const { $from, from, to, empty } = state.selection;
  const inSet = type.isInSet(state.storedMarks ?? $from.marks());
  if (inSet) return inSet;
  if (empty) {
    const before = $from.nodeBefore;
    return before ? (type.isInSet(before.marks) ?? null) : null;
  }
  let found: Mark | null = null;
  state.doc.nodesBetween(from, to, (node) => {
    if (found) return false;
    const mark = type.isInSet(node.marks);
    if (mark) found = mark;
    return true;
  });
  return found;
}

export const toggleStrong = toggleMark(marks.strong);
export const toggleEm = toggleMark(marks.em);
export const toggleUnderline = toggleMark(marks.underline);
export const toggleStrikethrough = toggleMark(marks.strikethrough);
export const toggleCodeMark = toggleMark(marks.code);
export const togglePlaceholder = toggleMark(marks.placeholder);

/**
 * Highlight carries a colour, so toggling is per colour: applying the colour
 * already there removes the mark, applying a different one replaces it, and
 * "None" (a `null` colour) removes it too.
 */
export function toggleHighlight(color: string | null): Command {
  return (state, dispatch, view) => {
    const current = markAt(state, marks.highlight);
    if (color === null) return toggleMark(marks.highlight)(state, dispatch, view);
    if (current && rawAttr(current, "color") === color) {
      return toggleMark(marks.highlight)(state, dispatch, view);
    }
    if (current && dispatch) {
      const { from, to } = state.selection;
      dispatch(
        state.tr
          .removeMark(from, to, marks.highlight)
          .addMark(from, to, marks.highlight.create({ color })),
      );
      return true;
    }
    return toggleMark(marks.highlight, { color })(state, dispatch, view);
  };
}

/** `Mod-K` and the "Create link" toolbar item. */
/**
 * Rewrite the link over an explicit range. The link toolbar edits the link the
 * caret is *inside*, where the selection is empty but the range is known
 * (`editor.md` §4); going through the selection would insert a second link
 * instead of changing the one on screen.
 */
export function setLinkOver(
  from: number,
  to: number,
  href: string,
  title: string | null = null,
): Command {
  return (state, dispatch) => {
    if (from >= to) return false;
    if (dispatch) {
      dispatch(
        state.tr
          .removeMark(from, to, marks.link)
          .addMark(from, to, marks.link.create({ href, title }))
          .scrollIntoView(),
      );
    }
    return true;
  };
}

export function setLink(href: string, title: string | null = null): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (empty) {
      // With no selection there is nothing to carry the mark; the caller
      // (the link toolbar) inserts the href as its own text instead.
      if (href === "") return false;
      if (dispatch) {
        const text = nodes.text.schema.text(href, [marks.link.create({ href, title })]);
        dispatch(state.tr.replaceSelectionWith(text, false).scrollIntoView());
      }
      return true;
    }
    if (dispatch) {
      dispatch(
        state.tr
          .removeMark(from, to, marks.link)
          .addMark(from, to, marks.link.create({ href, title }))
          .scrollIntoView(),
      );
    }
    return true;
  };
}

export const removeLink: Command = (state, dispatch) => {
  const range = linkRange(state);
  if (!range) return false;
  if (dispatch) dispatch(state.tr.removeMark(range.from, range.to, marks.link));
  return true;
};

export interface LinkRange {
  from: number;
  to: number;
  mark: Mark;
}

/** The full extent of the link mark under the caret or selection. */
export function linkRange(state: EditorState): LinkRange | null {
  const { $from, from, to } = state.selection;
  const mark = markAt(state, marks.link);
  if (!mark) return null;
  if (from !== to) return { from, to, mark };
  const index = $from.index();
  const parent = $from.parent;
  if (parent.childCount === 0) return null;
  let start = $from.start();
  let child = index < parent.childCount ? parent.child(index) : parent.child(parent.childCount - 1);
  let childIndex = Math.min(index, parent.childCount - 1);
  if (!mark.isInSet(child.marks) && childIndex > 0) childIndex -= 1;
  for (let i = 0; i < childIndex; i += 1) start += parent.child(i).nodeSize;
  child = parent.child(childIndex);
  let end = start + child.nodeSize;
  for (let i = childIndex - 1; i >= 0 && mark.isInSet(parent.child(i).marks); i -= 1) {
    start -= parent.child(i).nodeSize;
  }
  for (
    let i = childIndex + 1;
    i < parent.childCount && mark.isInSet(parent.child(i).marks);
    i += 1
  ) {
    end += parent.child(i).nodeSize;
  }
  return { from: start, to: end, mark };
}

/** `Mod-Enter` with the caret in a link: follow it (§10). */
export function followLink(open: (href: string) => void): Command {
  return (state) => {
    const range = linkRange(state);
    const href = range ? (stringAttr(range.mark, "href") ?? "") : "";
    if (href === "") return false;
    open(href);
    return true;
  };
}

/** `⇧-Enter`. */
export const insertHardBreak: Command = (state, dispatch) => {
  if (dispatch) {
    dispatch(state.tr.replaceSelectionWith(nodes.br.create()).scrollIntoView());
  }
  return true;
};

/**
 * `Mod-Space`: turn the selected text into inline math, or open an empty math
 * span at the caret. The delimiters are the editor's business only — the node
 * holds the raw LaTeX (`markdown-dialect.md` §6.12).
 */
export const insertInlineMathCommand: Command = (state, dispatch) => {
  const { from, to, empty } = state.selection;
  const content = empty ? "" : state.doc.textBetween(from, to, "");
  const node = nodes.mathInline.create(
    null,
    content === "" ? undefined : nodes.text.schema.text(content),
  );
  if (dispatch) {
    const tr = state.tr.replaceSelectionWith(node, false);
    const caret = Math.min(tr.mapping.map(from) + 1, tr.doc.content.size);
    dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(caret))).scrollIntoView());
  }
  return true;
};
